import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { BellIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../context/AuthContext';
import useNotificationStore from '../../stores/useNotificationStore';
import useAnalysisStore from '../../stores/useAnalysisStore';

const tabTranslations = {
  'context': 'Contexte (Atelier 1)',
  'business-values': 'Valeurs Métier (Atelier 1)',
  'events': 'Événements Redoutés (Atelier 1)',
  'security': 'Référentiel de Sécurité (Atelier 1)',
  'security-controls': 'Socle de Sécurité (Atelier 1)',
  'risk-treatment': 'Traitement des Risques (Atelier 1)',
  'atelier2-activite1': 'Sources de Risques (Atelier 2)',
  'atelier2-activite2': 'Objectifs Visés (Atelier 2)',
  'atelier2-activite3': 'Couples SR/OV (Atelier 2)',
  'atelier3-activite1': 'Parties Prenantes (Atelier 3)',
  'atelier3-activite2': 'Évaluation des PP (Atelier 3)',
  'atelier3-activite3': 'Cartographie (Atelier 3)',
  'atelier3-activite4': 'Chemins d\'Attaque (Atelier 3)',
  'atelier3-activite5': 'Évaluation des Menaces (Atelier 3)',
  'atelier4-threat-intelligence': 'Threat Intelligence (Atelier 4)',
  'atelier4-cti-scenarios': 'Scénarios Opérationnels (Atelier 4)',
  'atelier5-synthese-scenarios': 'Synthèse des Scénarios (Atelier 5)',
  'atelier5-strategie-traitement': 'Stratégie de Traitement (Atelier 5)',
  'atelier5-mesures-securite': 'Mesures de Sécurité (Atelier 5)',
  'atelier5-evaluation-residuels': 'Évaluation des Risques Résiduels (Atelier 5)',
  'atelier5-plan-traitement': 'Plan de Traitement (Atelier 5)',
  'atelier5-cadre-suivi': 'Cadre de Suivi (Atelier 5)',
  'atelier5-implementation-checklist': 'Suivi d\'Implémentation (Atelier 5)'
};

const NotificationCenter = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState({});
  const containerRef = useRef(null);
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);
  const { user } = useAuth();

  const notifications = useNotificationStore((s) => s.notifications);
  const unreadCount = useNotificationStore((s) => s.unreadCount);
  const markAsRead = useNotificationStore((s) => s.markAsRead);
  const markAllAsRead = useNotificationStore((s) => s.markAllAsRead);
  const currentAnalysis = useAnalysisStore((s) => s.currentAnalysis);

  const reposition = useCallback(() => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownStyle({
        top: rect.bottom + 8,
        right: window.innerWidth - rect.right
      });
    }
  }, []);

  const openDropdown = () => {
    reposition();
    setIsOpen(true);
  };

  const closeDropdown = useCallback(() => {
    setIsOpen(false);
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    reposition();
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [isOpen, reposition]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && containerRef.current.contains(event.target)) return;
      if (dropdownRef.current && dropdownRef.current.contains(event.target)) return;
      setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const formatTimeAgo = (date) => {
    const now = new Date();
    const d = new Date(date);
    const diffMin = Math.floor((now - d) / 60000);
    if (diffMin < 1) return "À l'instant";
    if (diffMin < 60) return `Il y a ${diffMin}min`;
    const diffHours = Math.floor(diffMin / 60);
    if (diffHours < 24) return `Il y a ${diffHours}h`;
    const diffDays = Math.floor(diffHours / 24);
    return `Il y a ${diffDays}j`;
  };

  const handleNotificationClick = (notification) => {
    const userId = user?._id || user?.id;
    if (userId && !(notification.readBy || []).includes(userId)) {
      markAsRead(notification._id, userId);
    }
    setIsOpen(false);
  };

  const handleMarkAllAsRead = () => {
    const userId = user?._id || user?.id;
    const analysisId = currentAnalysis?.id;
    if (analysisId && userId) {
      markAllAsRead(analysisId, userId);
    }
  };

  const userId = user?._id || user?.id;

  const dropdownPanel = (
    <div ref={dropdownRef} style={dropdownStyle} className="fixed w-80 bg-slate-800 rounded-lg shadow-2xl border border-slate-600/50 z-[9999]">
      <div className="px-3 py-2 border-b border-slate-600/50 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-white">Notifications</h3>
        <div className="flex items-center gap-2">
          {unreadCount > 0 && (
            <button
              onClick={handleMarkAllAsRead}
              className="text-[11px] text-blue-400 hover:text-blue-300"
            >
              Tout marquer comme lu
            </button>
          )}
          <button
            onClick={closeDropdown}
            className="p-0.5 rounded hover:bg-slate-600"
          >
            <XMarkIcon className="h-3 w-3 text-slate-400" />
          </button>
        </div>
      </div>

      <div className="max-h-80 overflow-y-auto">
        {notifications.length > 0 ? (
          <div className="divide-y divide-slate-700/50">
            {notifications.map((notification) => {
              const isUnread = userId && !(notification.readBy || []).includes(userId);
              return (
                <div
                  key={notification._id}
                  onClick={() => handleNotificationClick(notification)}
                  className={`px-3 py-2.5 hover:bg-slate-700/60 transition-colors cursor-pointer ${
                    isUnread ? 'bg-blue-900/20 border-l-2 border-blue-500' : ''
                  }`}
                >
                  <div className="flex items-start gap-2">
                    <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${
                      isUnread
                        ? 'bg-blue-500/20 text-blue-400'
                        : 'bg-slate-600/50 text-slate-400'
                    }`}>
                      {notification.actorName?.charAt(0)?.toUpperCase() || 'U'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-xs ${isUnread ? 'text-white font-medium' : 'text-slate-300'}`}>
                        <span className="font-semibold">{notification.actorName}</span>
                        {' a modifié '}
                        <span className="text-blue-300">{tabTranslations[notification.tab] || notification.tab}</span>
                      </p>
                      <p className="text-[10px] text-slate-500 mt-0.5">
                        {formatTimeAgo(notification.createdAt)}
                      </p>
                    </div>
                    {isUnread && (
                      <div className="w-1.5 h-1.5 rounded-full bg-blue-500 flex-shrink-0 mt-1.5"></div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="px-3 py-8 text-center">
            <BellIcon className="h-6 w-6 text-slate-600 mx-auto mb-2" />
            <p className="text-xs text-slate-500">Aucune notification</p>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="relative" ref={containerRef}>
      <button
        ref={buttonRef}
        onClick={() => (isOpen ? closeDropdown() : openDropdown())}
        className="relative p-1.5 rounded-lg text-slate-300 hover:text-white hover:bg-slate-700/60 transition-all duration-200 border border-slate-600/50"
        title="Notifications"
      >
        <BellIcon className="h-4 w-4" />
        {unreadCount > 0 && (
          <span className="absolute -top-1.5 -right-1.5 h-4 w-4 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center font-bold shadow-sm">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && ReactDOM.createPortal(dropdownPanel, document.body)}
    </div>
  );
};

export default NotificationCenter;
